
import React, { createContext, useContext, useState, useEffect } from 'react';

interface AccessibilityContextType {
  highContrast: boolean;
  fontSize: 'small' | 'medium' | 'large';
  reducedMotion: boolean;
  screenReader: boolean;
  toggleHighContrast: () => void;
  setFontSize: (size: 'small' | 'medium' | 'large') => void;
  toggleReducedMotion: () => void;
  setScreenReader: (enabled: boolean) => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

interface AccessibilityProviderProps {
  children: React.ReactNode;
}

export const AccessibilityProvider = ({ children }: AccessibilityProviderProps) => {
  const [highContrast, setHighContrast] = useState(false);
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [reducedMotion, setReducedMotion] = useState(false);
  const [screenReader, setScreenReader] = useState(false);

  // Detect system preferences
  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setReducedMotion(prefersReducedMotion);

    // Check for high contrast preference
    const prefersHighContrast = window.matchMedia('(prefers-contrast: high)').matches;
    setHighContrast(prefersHighContrast);

    // Detect screen reader usage - convert to boolean properly
    const isScreenReader = window.navigator.userAgent.includes('NVDA') || 
                          window.navigator.userAgent.includes('JAWS') || 
                          !!window.speechSynthesis;
    setScreenReader(isScreenReader);
  }, []);

  // Apply accessibility classes to document
  useEffect(() => {
    const classes = [];
    
    if (highContrast) classes.push('high-contrast');
    if (fontSize === 'large') classes.push('large-text');
    if (fontSize === 'small') classes.push('small-text');
    if (reducedMotion) classes.push('reduced-motion');
    if (screenReader) classes.push('screen-reader-active');

    document.documentElement.className = classes.join(' ');
    
    // Set CSS custom properties for accessibility
    document.documentElement.style.setProperty('--font-size-multiplier', 
      fontSize === 'large' ? '1.25' : fontSize === 'small' ? '0.875' : '1'
    );
  }, [highContrast, fontSize, reducedMotion, screenReader]);

  const toggleHighContrast = () => setHighContrast(!highContrast);
  const toggleReducedMotion = () => setReducedMotion(!reducedMotion);

  const value = {
    highContrast,
    fontSize,
    reducedMotion,
    screenReader,
    toggleHighContrast,
    setFontSize,
    toggleReducedMotion,
    setScreenReader
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};
